import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { STEP_UP_TOKEN_TTL_MS } from "../../application/hooks/useStepUpVerification";
import { useSignInMethodChange } from "../../application/hooks/useSignInMethodChange";
import { useAuthenticatorSetup } from "../../application/hooks/useAuthenticatorSetup";
import { SignInMethodChangeError } from "@/features/user-settings/infrastructure/api/userSettingsApi";
import { AUTH_FLOW_PURPOSE_CHANGE_OTP_PREFERENCE } from "../../domain/constants/authFlowConstants";
import {
  OTP_PREFERENCE_EMAIL,
  OTP_PREFERENCE_TOTP,
  type OtpPreference,
} from "../../domain/constants/otpConstants";
import type { QrCode } from "../../domain/entities/QrCode";
import { AuthenticatorSetupStep } from "./AuthenticatorSetupStep";
import { PasswordField } from "./PasswordField";
import { VerifyIdentityStep } from "./VerifyIdentityStep";

type Step = "currentPassword" | "authenticatorSetup" | "verify" | "saveFailed";

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string | null;
  totpEnabled: boolean;
  currentMethod: OtpPreference;
  /** The method picked in Settings; the dialog is only open while this is set. */
  targetMethod: OtpPreference | null;
  onAuthenticatorEnabled?: () => void;
  onChanged: (method: OtpPreference) => void;
}

/**
 * Changing the sign-in method: current password (checked up front) →
 * authenticator setup when switching to one that isn't configured → code
 * from the current sign-in method for CHANGE_OTP_PREFERENCE → save with that
 * temp token and the password.
 */
export const ChangeSignInMethodDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  email,
  totpEnabled,
  currentMethod,
  targetMethod,
  onAuthenticatorEnabled,
  onChanged,
}) => {
  const { t } = useTranslation("settings");
  const {
    verifyCurrentPassword,
    saveSignInMethod,
    loading: saving,
  } = useSignInMethodChange();
  const { startAuthenticatorSetup, loading: loadingSetup } = useAuthenticatorSetup();
  const loading = saving || loadingSetup;

  const [step, setStep] = useState<Step>("currentPassword");
  const [currentPassword, setCurrentPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [tempTokenExpiresAt, setTempTokenExpiresAt] = useState(0);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<QrCode | null>(null);
  const [setUpAuthenticatorHere, setSetUpAuthenticatorHere] = useState(false);
  // Fixed once the password is accepted so the step count doesn't change mid-flow.
  const [includesSetup, setIncludesSetup] = useState(false);

  const methodName = (method: OtpPreference) =>
    method === OTP_PREFERENCE_TOTP
      ? t("verifyIdentity.methodAuthenticator")
      : t("verifyIdentity.methodEmail");

  const needsAuthenticatorSetup =
    targetMethod === OTP_PREFERENCE_TOTP && !totpEnabled && !setUpAuthenticatorHere;
  const totalSteps = includesSetup ? 3 : 2;
  const stepNumber =
    step === "currentPassword" ? 1 : step === "authenticatorSetup" ? 2 : totalSteps;

  const reset = () => {
    setStep("currentPassword");
    setCurrentPassword("");
    setPasswordError(null);
    setTempToken(null);
    setTempTokenExpiresAt(0);
    setVerifyNotice(null);
    setSaveError(null);
    setQrCode(null);
    setIncludesSetup(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (loading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const restartVerification = () => {
    setTempToken(null);
    setSaveError(null);
    setVerifyNotice(t("signInMethodDialog.verificationExpired"));
    setStep("verify");
  };

  const handleCurrentPasswordNext = async () => {
    if (!currentPassword) {
      setPasswordError(t("signInMethodDialog.currentPasswordRequired"));
      return;
    }
    try {
      await verifyCurrentPassword(currentPassword);
    } catch (err) {
      setPasswordError(errorMessage(err, t("signInMethodDialog.verifyPasswordError")));
      return;
    }

    setVerifyNotice(null);
    if (!needsAuthenticatorSetup) {
      setStep("verify");
      return;
    }
    try {
      // A fresh secret is issued per call, so only fetch once per dialog session.
      if (!qrCode) setQrCode(await startAuthenticatorSetup());
      setIncludesSetup(true);
      setStep("authenticatorSetup");
    } catch (err) {
      setPasswordError(errorMessage(err, t("verifyIdentity.setupLoadError")));
    }
  };

  const handleSetupConfirmed = () => {
    setSetUpAuthenticatorHere(true);
    onAuthenticatorEnabled?.();
    setStep("verify");
  };

  const save = async (token: string) => {
    if (!targetMethod) return;
    try {
      await saveSignInMethod(token, targetMethod, currentPassword);
      toast.success(
        t("signInMethodDialog.successToast", { method: methodName(targetMethod) }),
      );
      onChanged(targetMethod);
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof SignInMethodChangeError && err.status === 401) {
        restartVerification();
        return;
      }
      setSaveError(errorMessage(err, t("signInMethodDialog.saveError")));
      setStep("saveFailed");
    }
  };

  const handleVerified = async (token: string) => {
    setTempToken(token);
    setTempTokenExpiresAt(Date.now() + STEP_UP_TOKEN_TTL_MS);
    await save(token);
  };

  const handleRetry = () => {
    if (!tempToken || Date.now() >= tempTokenExpiresAt) {
      restartVerification();
      return;
    }
    void save(tempToken);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (step === "currentPassword") void handleCurrentPasswordNext();
    else if (step === "saveFailed") handleRetry();
  };

  if (!targetMethod) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-xl sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("signInMethodDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("stepOf", { step: stepNumber, total: totalSteps })}
            {" · "}
            {step === "currentPassword"
              ? t("signInMethodDialog.stepTitles.currentPassword")
              : step === "authenticatorSetup"
                ? t("signInMethodDialog.stepTitles.authenticatorSetup")
                : t("signInMethodDialog.stepTitles.verify")}
          </DialogDescription>
        </DialogHeader>

        {step === "authenticatorSetup" && qrCode ? (
          <AuthenticatorSetupStep
            idPrefix="sign-in-method"
            qrCode={qrCode}
            onConfirmed={handleSetupConfirmed}
            onBack={() => setStep("currentPassword")}
          />
        ) : step === "verify" ? (
          <VerifyIdentityStep
            purpose={AUTH_FLOW_PURPOSE_CHANGE_OTP_PREFERENCE}
            email={email}
            totpEnabled={totpEnabled}
            defaultMethod={OTP_PREFERENCE_EMAIL}
            // The API only accepts an email code for CHANGE_OTP_PREFERENCE.
            onlyMethod={OTP_PREFERENCE_EMAIL}
            notice={verifyNotice}
            idPrefix="sign-in-method"
            onAuthenticatorEnabled={onAuthenticatorEnabled}
            onVerified={handleVerified}
            onBack={() => setStep("currentPassword")}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {step === "currentPassword" && (
              <>
                <p className="text-sm text-gray-500">
                  {t("signInMethodDialog.currentPasswordHint", {
                    from: methodName(currentMethod),
                    to: methodName(targetMethod),
                  })}
                </p>
                <PasswordField
                  id="sign-in-method-current-password"
                  label={t("signInMethodDialog.currentPasswordLabel")}
                  value={currentPassword}
                  onChange={(value) => {
                    setCurrentPassword(value);
                    setPasswordError(null);
                  }}
                  error={passwordError}
                  disabled={loading}
                  autoFocus
                  autoComplete="current-password"
                />
              </>
            )}

            {step === "saveFailed" && saveError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{saveError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button className="cursor-pointer"
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={loading}
              >
                {t("signInMethodDialog.cancel")}
              </Button>
              <Button className="cursor-pointer" type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {step === "currentPassword"
                  ? t("signInMethodDialog.next")
                  : t("signInMethodDialog.retry")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
